import { generateEntrySlug } from 'features/rolodex'
import {
  type RolodexEntryJson,
  type RolodexEntryPatchJson,
  type RolodexEntryPutJson,
  RolodexEntryType,
  type RolodexLoadedEntryJson,
} from 'types'

import { isLabelGroupId, type LabelGroupId } from './labelGroups'

/**
 * A site-bound annotation over a span of process-data time.
 *
 * `sensorIds` holds the same pen identifier the ruler tool persists in
 * `selected_sensor_ids` — the sensor (datapoint) Rolodex entry ID, e.g.
 * `longridge-DWATT2`. It is what `getSeriesInfoFromUPlot` reports as
 * `sensorId` and what `SourceConfig.sourceId` carries for sensor pens.
 */
export interface ProcessDataLabel {
  id: string
  name: string
  description: string
  startMs: number
  endMs: number
  sensorIds: string[]
  /** `null` renders in the default amber. */
  groupId: LabelGroupId | null
  evalKind?: LabelEvalKind | null
}

export type LabelEvalKind = 'anomaly' | 'normal'

const isLabelEvalKind = (value: unknown): value is LabelEvalKind =>
  value === 'anomaly' || value === 'normal'

/** The snake_case `data` blob a `fde-process-data-label` entry stores. */
export type ProcessDataLabelData = {
  name: string
  description: string
  start_time: number
  end_time: number
  sensor_ids: string[]
  group_id: string | null
  eval?: { kind: LabelEvalKind }
}

export const PROCESS_DATA_LABEL_LOAD_LIMIT = 1000

/**
 * Load options for a site's labels.
 *
 * Filtering happens server-side: `/entries/load` truncates at `limit` in an
 * unstable order, so a load-then-filter-client-side query would silently drop
 * labels once a site's other entries of this type grow past the limit.
 */
export const buildProcessDataLabelsLoadOptions = (
  siteID: string | undefined
): {
  type: string[]
  tags?: Record<string, string[]>
  limit: number
} =>
  siteID
    ? {
        type: [RolodexEntryType.PROCESS_DATA_LABEL],
        tags: { site: [siteID] },
        limit: PROCESS_DATA_LABEL_LOAD_LIMIT,
      }
    : { type: [], limit: 0 }

export const generateProcessDataLabelID = (): string =>
  generateEntrySlug(RolodexEntryType.PROCESS_DATA_LABEL)

const normalizeSensorIds = (sensorIds: readonly string[]): string[] => {
  const normalized: string[] = []
  const seen = new Set<string>()

  sensorIds.forEach((sensorId) => {
    const trimmed = sensorId.trim()
    if (!trimmed || seen.has(trimmed)) return
    seen.add(trimmed)
    normalized.push(trimmed)
  })

  return normalized
}

/**
 * Serialize a label into its stored form.
 *
 * Start/end are ordered here rather than at the call site: a region drawn
 * right-to-left arrives reversed, and a stored `start_time > end_time` would
 * render as a zero-or-negative-width region on every later read.
 */
export const serializeProcessDataLabel = (
  label: Omit<ProcessDataLabel, 'id'>
): ProcessDataLabelData => ({
  name: label.name.trim(),
  description: label.description.trim(),
  start_time: Math.min(label.startMs, label.endMs),
  end_time: Math.max(label.startMs, label.endMs),
  sensor_ids: normalizeSensorIds(label.sensorIds),
  group_id: isLabelGroupId(label.groupId) ? label.groupId : null,
  ...(isLabelEvalKind(label.evalKind)
    ? { eval: { kind: label.evalKind } }
    : {}),
})

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const getRolodexEntryJson = (value: unknown): RolodexEntryJson | null => {
  if (!isRecord(value)) return null

  const candidateEntry = isRecord(value.entry)
    ? (value as RolodexLoadedEntryJson).entry
    : value

  return isRecord(candidateEntry) &&
    typeof candidateEntry.id === 'string' &&
    typeof candidateEntry.type === 'string' &&
    isRecord(candidateEntry.data)
    ? (candidateEntry as RolodexEntryJson)
    : null
}

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return null
}

/**
 * Parse one loaded entry. Returns `null` — rather than throwing — for anything
 * that is not a usable label, so one malformed entry cannot take a site's whole
 * label list down with it.
 */
export const parseProcessDataLabel = (
  entry: unknown
): ProcessDataLabel | null => {
  const rolodexEntry = getRolodexEntryJson(entry)
  if (!rolodexEntry) return null
  if (rolodexEntry.type !== RolodexEntryType.PROCESS_DATA_LABEL) return null

  const data = rolodexEntry.data
  const startMs = toFiniteNumber(data.start_time)
  const endMs = toFiniteNumber(data.end_time)
  if (startMs === null || endMs === null) return null

  const sensorIds = Array.isArray(data.sensor_ids)
    ? normalizeSensorIds(
        data.sensor_ids.filter(
          (sensorId): sensorId is string => typeof sensorId === 'string'
        )
      )
    : []

  const evalBlock = isRecord(data.eval) ? data.eval : null

  return {
    id: rolodexEntry.id,
    name: typeof data.name === 'string' ? data.name : '',
    description: typeof data.description === 'string' ? data.description : '',
    startMs: Math.min(startMs, endMs),
    endMs: Math.max(startMs, endMs),
    sensorIds,
    groupId: isLabelGroupId(data.group_id) ? data.group_id : null,
    evalKind: isLabelEvalKind(evalBlock?.kind) ? evalBlock.kind : null,
  }
}

export const parseProcessDataLabels = (
  entries: readonly unknown[] | undefined
): ProcessDataLabel[] =>
  (entries ?? []).flatMap((entry) => parseProcessDataLabel(entry) ?? [])

/**
 * Build the upsert payload for a new label.
 *
 * Tags carry the site only: tags name the parent scope, and the label's own
 * identity (its pens, its range) lives in `data` where readers match on it.
 */
export const buildProcessDataLabelEntry = (
  id: string,
  siteID: string,
  label: Omit<ProcessDataLabel, 'id'>
): RolodexEntryPutJson<ProcessDataLabelData> => ({
  id,
  type: RolodexEntryType.PROCESS_DATA_LABEL,
  data: serializeProcessDataLabel(label),
  tags: { site: siteID },
})

export const buildProcessDataLabelPatch = (
  label: ProcessDataLabel
): RolodexEntryPatchJson<ProcessDataLabelData> => ({
  id: label.id,
  type: RolodexEntryType.PROCESS_DATA_LABEL,
  data: serializeProcessDataLabel(label),
})
